import axios from 'axios';
import { auth } from '../firebase'; // Import the initialized auth instance

// API Configuration
const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increased to 30 seconds for token verification delays
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to get auth headers - called before each request
async function getAuthHeaders() {
  const headers = {
    'Content-Type': 'application/json',
  };
  
  try {
    const currentUser = auth.currentUser;
    console.log('🔐 Getting auth for user:', currentUser?.uid || 'null');
    
    if (currentUser) {
      const token = await currentUser.getIdToken(true);
      headers.Authorization = `Bearer ${token}`;
      console.log('🔑 Auth token obtained');
    } else {
      console.warn('⚠️ No authenticated user');
    }
  } catch (error) {
    console.error('❌ Failed to get auth token:', error);
  }
  
  return headers;
}

// ============================================================================
// DATABASE SERVICE - BROWSER COMPATIBLE (API CALLS ONLY)
// ============================================================================

const dbService = {
  // ============================================================================
  // USER OPERATIONS
  // ============================================================================

  async getUsers({
    search = '',
    status = '',
    limit = 10,
    offset = 0,
    orderBy = 'created_at',
    order = 'desc',
  } = {}) {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (status) params.append('status', status);
      if (limit) params.append('limit', limit.toString());
      if (offset) params.append('offset', offset.toString());
      if (orderBy) params.append('orderBy', orderBy);
      if (order) params.append('order', order);

      // Get auth headers explicitly
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/users?${params.toString()}`, { headers });

      return {
        data: response.data.data || [],
        total: response.data.total || 0,
        pagination: response.data.pagination || {},
        error: null,
      };
    } catch (error) {
      console.error('Get users failed:', error);
      return {
        data: [],
        total: 0,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  async getUserById(id) {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/users/${id}`, { headers });
      return response.data.data || null;
    } catch (error) {
      console.error('Get user by ID failed:', error);
      return null;
    }
  },

  async createUser(userData) {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.post('/users', userData, { headers });
      return response.data.data || null;
    } catch (error) {
      console.error('Create user failed:', error);
      throw error;
    }
  },

  async updateUser(id, updates) {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.put(`/users/${id}`, updates, { headers });
      return response.data.data || null;
    } catch (error) {
      console.error('Update user failed:', error);
      throw error;
    }
  },

  async deleteUser(id) {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.delete(`/users/${id}`, { headers });
      return response.data.success || false;
    } catch (error) {
      console.error('Delete user failed:', error);
      throw error;
    }
  },

  async getAllUsersForExport() {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get('/users/export', { headers });
      return {
        data: response.data.data || [],
        total: response.data.total || 0,
        error: null,
      };
    } catch (error) {
      console.error('Get users for export failed:', error);
      return {
        data: [],
        total: 0,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  // ============================================================================
  // TRANSACTION OPERATIONS
  // ============================================================================

  async addTransaction(transactionData) {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.post('/transactions', transactionData, { headers });
      return response.data.data || null;
    } catch (error) {
      console.error('Add transaction failed:', error);
      throw error;
    }
  },

  async getAllTransactions({ limit = 50, offset = 0, userId = null } = {}) {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (offset) params.append('offset', offset.toString());
      if (userId) params.append('userId', userId);

      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/transactions?${params.toString()}`, { headers });
      return {
        data: response.data.data || [],
        total: response.data.count || 0,
        error: null,
      };
    } catch (error) {
      console.error('Get all transactions failed:', error);
      return {
        data: [],
        total: 0,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  async getUserTransactions(userId) {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/transactions/user/${userId}`, { headers });
      return response.data.data || [];
    } catch (error) {
      console.error('Get user transactions failed:', error);
      return [];
    }
  },

  // ============================================================================
  // DIVIDEND OPERATIONS
  // ============================================================================

  async checkDividendsExist(year) {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/dividends/exists/${year}`, { headers });
      return {
        exists: response.data.exists || false,
        count: response.data.count || 0,
      };
    } catch (error) {
      console.error('Check dividends exist failed:', error);
      return { exists: false, count: 0 };
    }
  },

  async processDividends(year, multiplier = 0.09384, processedBy = 'system') {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.post('/dividends/process', {
        year: year,
        multiplier: multiplier,
        processed_by: processedBy,
      }, { headers });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Process dividends failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  async getDividendsByYear(year) {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/dividends/year/${year}`, { headers });
      return response.data.data || [];
    } catch (error) {
      console.error('Get dividends by year failed:', error);
      return [];
    }
  },

  async getDividendSummary(year) {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/dividends/summary/${year}`, { headers });
      return response.data.data || null;
    } catch (error) {
      console.error('Get dividend summary failed:', error);
      return null;
    }
  },

  async getAllDividends({ limit = 100, offset = 0, year = null } = {}) {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (offset) params.append('offset', offset.toString());
      if (year) params.append('year', year);

      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/dividends?${params.toString()}`, { headers });
      return {
        data: response.data.data || [],
        total: response.data.count || 0,
        error: null,
      };
    } catch (error) {
      console.error('Get all dividends failed:', error);
      return {
        data: [],
        total: 0,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  // ============================================================================
  // STAFF OPERATIONS
  // ============================================================================

  async getStaff() {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get('/staff', { headers });
      return response.data.data || [];
    } catch (error) {
      console.error('Get staff failed:', error);
      return [];
    }
  },

  async getStaffById(id) {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/staff/${id}`, { headers });
      return response.data.data || null;
    } catch (error) {
      // Return null for staff not found (common case)
      return null;
    }
  },

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  async testConnection() {
    try {
      const response = await apiClient.get('/health');
      console.log(' API connection test:', response.data.message);
      return { success: true, data: response.data };
    } catch (error) {
      console.error(' API connection test failed:', error.message);
      return { success: false, error: error.message };
    }
  },
};

export default dbService;
